/*
 * Cài đặt Telesale dùng chung (A25, /api/v2/telesale/settings): ngưỡng vận hành, chia số theo nhóm, chia lại khách gọi điện, chia lại chat.
 * Các màn vẫn đọc, ghi store như cũ. File này tải cài đặt từ server vào store và gửi thay đổi lên server.
 * Bản trong localStorage chỉ còn là bản đệm khi chưa tải được.
 */
import { useEffect } from 'react';
import { message } from 'antd';
import useAuthStore from '../../store/authStore';
import { DEFAULT_CFG, type Cfg } from './dashboard/logic';
import useTeleDash from './dashboard/store';
import { apiRequest } from './lead/api';
import { DEFAULT_GROUP_CFG, useGroupCfg, type GroupCfg } from './lead/groups';
import { DEFAULT_CHAT_REASSIGN, DEFAULT_REASSIGN, type ChatReassignCfg, type ReassignCfg } from './lead/reassign';
import { useLeadStore } from './lead/store';

const URL = '/api/v2/telesale/settings';
const TTL = 60_000;
const SAVE_DELAY = 500;

export interface TeleSettings {
  dashboard: Cfg | null;
  groups: GroupCfg | null;
  reassign: ReassignCfg | null;
  chat_reassign: ChatReassignCfg | null;
  updated_at?: string | null;
  updated_by?: unknown;
}
export type SectionKey = 'dashboard' | 'groups' | 'reassign' | 'chat_reassign';
export const SECTIONS: SectionKey[] = ['dashboard', 'groups', 'reassign', 'chat_reassign'];
export type SectionValues = Record<SectionKey, unknown>;

export const SECTION_DEFAULTS: SectionValues = { dashboard: DEFAULT_CFG, groups: DEFAULT_GROUP_CFG, reassign: DEFAULT_REASSIGN, chat_reassign: DEFAULT_CHAT_REASSIGN };

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Trường server chưa có (bản FE mới thêm) lấy theo mặc định */
export const withDefaults = <T extends object>(value: T, defaults: T): T => ({ ...defaults, ...value });

/** Lần đầu nối: mục server còn trống mà máy đã chỉnh khác mặc định thì gửi lên một lần */
export const sectionsToUpload = (server: Partial<Record<SectionKey, unknown>>, local: SectionValues, defaults: SectionValues = SECTION_DEFAULTS) =>
  SECTIONS.filter((k) => server[k] == null && !same(local[k], defaults[k]));

const readLocal = (): SectionValues => {
  const lead = useLeadStore.getState();
  return { dashboard: useTeleDash.getState().cfg, groups: useGroupCfg.getState().cfg, reassign: lead.reassignCfg, chat_reassign: lead.chatCfg };
};

const canWrite = () => {
  const a = useAuthStore.getState();
  return a.isSuperAdmin || a.hasPermission('telesales.config') || a.hasPermission('telesales.assign');
};

/** Bản server biết gần nhất của từng mục: khác bản này mới gửi lên */
const known: Partial<Record<SectionKey, string>> = {};
let applying = false;
let loadedAt = 0;
let loading: Promise<void> | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

const writeStore = (k: SectionKey, value: unknown) => {
  if (k === 'dashboard') useTeleDash.setState({ cfg: value as Cfg });
  else if (k === 'groups') useGroupCfg.setState({ cfg: value as GroupCfg });
  else if (k === 'reassign') useLeadStore.setState({ reassignCfg: value as ReassignCfg });
  else useLeadStore.setState({ chatCfg: value as ChatReassignCfg });
};

const applyServer = (data: TeleSettings) => {
  const local = readLocal();
  applying = true;
  SECTIONS.forEach((k) => {
    const v = data[k];
    if (v == null) return;
    const merged = withDefaults(v as object, SECTION_DEFAULTS[k] as object);
    known[k] = JSON.stringify(merged);
    if (!same(local[k], merged)) writeStore(k, merged);
  });
  applying = false;
};

const put = (body: Partial<Record<SectionKey, unknown>>) => apiRequest<TeleSettings>(URL, { method: 'PUT', body: JSON.stringify(body) });

const load = async (force = false) => {
  if (!force && Date.now() - loadedAt < TTL) return;
  if (loading) { await loading; return; }
  loading = (async () => {
    const r = await apiRequest<TeleSettings>(URL);
    // Không có quyền đọc (Tư vấn viên...) hoặc môi trường chưa có API: giữ bản trên máy, không báo lỗi
    if (!r.ok || !r.data) return;
    loadedAt = Date.now();
    applyServer(r.data);
    const local = readLocal();
    SECTIONS.forEach((k) => { if (r.data![k] == null) known[k] = JSON.stringify(SECTION_DEFAULTS[k]); });
    const missing = sectionsToUpload(r.data, local);
    if (!missing.length || !canWrite()) return;
    const up = await put(Object.fromEntries(missing.map((k) => [k, local[k]])));
    if (up.ok && up.data) applyServer(up.data);
  })().finally(() => { loading = null; });
  await loading;
};

const flush = async () => {
  const local = readLocal();
  const changed = SECTIONS.filter((k) => known[k] !== undefined && known[k] !== JSON.stringify(local[k]));
  if (!changed.length || !canWrite()) return;
  const r = await put(Object.fromEntries(changed.map((k) => [k, local[k]])));
  if (r.ok && r.data) { applyServer(r.data); return; }
  // Server từ chối (quyền, dữ liệu sai): hiện lại bản trên server. Mất mạng thì giữ để gửi ở lần đổi sau
  message.error(`Chưa lưu được cài đặt lên server: ${r.message}`);
  if (r.status && r.status < 500) load(true);
};

const onLocalChange = () => {
  if (applying) return;
  clearTimeout(timer);
  timer = setTimeout(flush, SAVE_DELAY);
};

let started = false;
const start = () => {
  if (started) return;
  started = true;
  useTeleDash.subscribe((s, p) => { if (s.cfg !== p.cfg) onLocalChange(); });
  useGroupCfg.subscribe((s, p) => { if (s.cfg !== p.cfg) onLocalChange(); });
  useLeadStore.subscribe((s, p) => { if (s.reassignCfg !== p.reassignCfg || s.chatCfg !== p.chatCfg) onLocalChange(); });
};

/** Gọi ở các màn đọc cài đặt: tải bản trên server, tối đa mỗi phút một lần */
export function useTeleSettingsSync() {
  useEffect(() => {
    start();
    load();
  }, []);
}
